<template>
    <v-container fluid>
        <v-row class="align-center pl-3">
            <v-breadcrumbs
                :items="[
                    { text: this.$t('home.home'), disable: false, href: '/#/' },
                    { text: this.$t('printing.title'), disabled: true }
                ]"
                divider="/"
            ></v-breadcrumbs>
            <v-spacer />
        </v-row>

        <div class="mx-3 mb-4">
            <div class="subtitle-1 grey--text text--darken-2">
                {{ $t("printing.page_description") }}
            </div>
        </div>

        <!-- Create agent dialog (name prompt) -->
        <v-dialog v-model="createAgentDialog" max-width="450px">
            <v-card>
                <v-card-title>{{
                    $t("printing.add_print_agent")
                }}</v-card-title>
                <v-card-text>
                    <v-form
                        ref="createAgentForm"
                        v-model="createAgentFormValid"
                        @submit.prevent="createAgentConfirmed"
                    >
                        <v-text-field
                            v-model="newAgentName"
                            :label="$t('printing.agent_name')"
                            :hint="$t('printing.agent_name_hint')"
                            persistent-hint
                            counter="50"
                            :rules="[
                                v =>
                                    (!!v && !!v.trim()) ||
                                    $t('printing.name_required')
                            ]"
                            autofocus
                        ></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="createAgentDialog = false">{{
                        $t("printing.cancel")
                    }}</v-btn>
                    <v-btn
                        color="primary"
                        outlined
                        rounded
                        :disabled="!createAgentFormValid"
                        :loading="isCreatingAgent"
                        @click="createAgentConfirmed"
                        >{{ $t("printing.create") }}</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Agent created: show key once + wait for connection -->
        <PrintAgentCreatedDialog
            :dialog.sync="agentCreatedDialog"
            :rawKey="createdRawKey"
            :agentId="createdAgentId"
            v-on:agent-connected="refreshAll"
        />

        <!-- Rename agent dialog -->
        <v-dialog v-model="renameAgentDialog" max-width="450px">
            <v-card>
                <v-card-title>{{
                    $t("printing.rename_agent")
                }}</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="renameAgentName"
                        :label="$t('printing.agent_name')"
                        counter="50"
                    ></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="renameAgentDialog = false">{{
                        $t("printing.cancel")
                    }}</v-btn>
                    <v-btn
                        color="primary"
                        outlined
                        rounded
                        :loading="isSavingAgent"
                        @click="renameAgentConfirmed"
                        >{{ $t("printing.save") }}</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Revoke agent dialog -->
        <DeleteDialog
            :dialog.sync="revokeAgentDialog"
            :item="revokedAgent"
            v-on:confirmed="revokeAgentConfirmed"
        >
            <template v-slot:title
                >{{ $t("printing.revoke_agent") }} -
                {{ revokedAgent ? revokedAgent.name : "" }}</template
            >
            <template v-slot:content>{{
                $t("printing.revoke_agent_confirmation")
            }}</template>
        </DeleteDialog>

        <!-- Printer edit / create dialog -->
        <v-dialog v-model="printerDialog" max-width="500px">
            <v-card>
                <v-card-title>
                    {{
                        editedPrinter && editedPrinter.id
                            ? $t("printing.edit_printer")
                            : $t("printing.add_printer")
                    }}
                </v-card-title>
                <v-card-text v-if="editedPrinter">
                    <v-form
                        ref="printerForm"
                        v-model="printerFormValid"
                        @submit.prevent="savePrinter"
                    >
                        <v-text-field
                            v-model="editedPrinter.name"
                            :label="$t('printing.printer_name')"
                            counter="100"
                            :rules="[
                                v =>
                                    (!!v && !!v.trim()) ||
                                    $t('printing.name_required')
                            ]"
                        ></v-text-field>
                        <v-text-field
                            v-model="editedPrinter.address"
                            :label="$t('printing.printer_address')"
                            :hint="$t('printing.printer_address_hint')"
                            persistent-hint
                            :disabled="editedPrinter.isAutoDiscovered"
                            :rules="
                                editedPrinter.isAutoDiscovered
                                    ? []
                                    : [
                                          v =>
                                              (!!v && !!v.trim()) ||
                                              $t('printing.address_required')
                                      ]
                            "
                        ></v-text-field>
                        <v-select
                            v-model="editedPrinter.dpi"
                            :items="[203, 300]"
                            :label="$t('printing.printer_dpi')"
                        ></v-select>
                        <v-select
                            v-if="!editedPrinter.isAutoDiscovered"
                            v-model="editedPrinter.servedByAgentId"
                            :items="agentChoices"
                            :label="$t('printing.served_by_agent')"
                            :rules="
                                editedPrinter.id
                                    ? []
                                    : [
                                          v =>
                                              !!v ||
                                              $t('printing.agent_required')
                                      ]
                            "
                        ></v-select>
                        <v-switch
                            v-if="editedPrinter.id"
                            v-model="editedPrinter.isActive"
                            :label="$t('printing.printer_active')"
                        ></v-switch>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="printerDialog = false">{{
                        $t("printing.cancel")
                    }}</v-btn>
                    <v-btn
                        color="primary"
                        outlined
                        rounded
                        :disabled="!printerFormValid"
                        :loading="isSavingPrinter"
                        @click="savePrinter"
                        >{{ $t("printing.save") }}</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Print agents card (admin only) -->
        <v-row v-if="isAdmin" class="ma-0">
            <v-col cols="12" class="pt-0">
                <v-card outlined>
                    <v-card-title>
                        <v-icon color="primary" class="mx-2">dns</v-icon>
                        {{ $t("printing.print_agents") }}
                        <v-chip x-small outlined class="ml-2" color="grey">{{
                            activeAgents.length
                        }}</v-chip>
                        <v-spacer></v-spacer>
                        <v-btn
                            color="primary"
                            outlined
                            rounded
                            small
                            @click="openCreateAgent"
                            >{{ $t("printing.add_print_agent") }}</v-btn
                        >
                    </v-card-title>
                    <v-card-subtitle>{{
                        $t("printing.print_agents_subtitle")
                    }}</v-card-subtitle>
                    <v-container fluid class="pt-0">
                        <v-data-table
                            :headers="agentHeaders"
                            :items="activeAgents"
                            :loading="$apollo.queries.printAgents.loading"
                            :items-per-page="10"
                            hide-default-footer
                            :no-data-text="$t('printing.no_agents')"
                        >
                            <template v-slot:item.isOnline="{ item }">
                                <v-chip
                                    v-if="item.isOnline"
                                    small
                                    color="success"
                                    text-color="white"
                                    >{{ $t("printing.online") }}</v-chip
                                >
                                <v-chip v-else small color="grey" outlined>{{
                                    item.lastUsedAt
                                        ? $t("printing.last_seen") +
                                          " " +
                                          formatFromNow(item.lastUsedAt)
                                        : $t("printing.never_connected")
                                }}</v-chip>
                            </template>
                            <template v-slot:item.created="{ item }">
                                {{ formatDate(item.created) }}
                            </template>
                            <template v-slot:item.action="{ item }">
                                <v-btn
                                    icon
                                    outlined
                                    small
                                    color="primary"
                                    class="mr-1"
                                    @click="openRenameAgent(item)"
                                >
                                    <v-icon small>edit</v-icon>
                                </v-btn>
                                <v-btn
                                    icon
                                    outlined
                                    small
                                    color="error"
                                    @click="openRevokeAgent(item)"
                                >
                                    <v-icon small>block</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>
                    </v-container>
                </v-card>
            </v-col>
        </v-row>

        <!-- Printers & queue tabs -->
        <v-row class="ma-0">
            <v-col cols="12" class="pt-0">
                <v-card outlined>
                    <v-tabs v-model="tab">
                        <v-tab>{{ $t("printing.printers") }}</v-tab>
                        <v-tab>{{ $t("printing.print_queue") }}</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="tab">
                        <!-- Printers tab -->
                        <v-tab-item>
                            <v-container fluid>
                                <v-row class="ma-0 mb-2" align="center">
                                    <v-spacer></v-spacer>
                                    <v-btn
                                        v-if="isAdmin"
                                        color="primary"
                                        outlined
                                        rounded
                                        small
                                        @click="openCreatePrinter"
                                        >{{
                                            $t("printing.add_printer")
                                        }}</v-btn
                                    >
                                </v-row>
                                <v-data-table
                                    :headers="printerHeaders"
                                    :items="printers"
                                    :loading="
                                        $apollo.queries.tenantPrinters.loading
                                    "
                                    :items-per-page="25"
                                    :no-data-text="$t('printing.no_printers')"
                                >
                                    <template v-slot:item.address="{ item }">
                                        <span style="font-family: monospace;">{{
                                            item.address
                                        }}</span>
                                        <div
                                            v-if="item.lastReportedAt"
                                            class="caption grey--text"
                                        >
                                            {{ $t("printing.last_reported") }}
                                            {{
                                                formatFromNow(
                                                    item.lastReportedAt
                                                )
                                            }}
                                        </div>
                                    </template>
                                    <template v-slot:item.isActive="{ item }">
                                        <v-chip
                                            v-if="item.isActive"
                                            small
                                            color="success"
                                            text-color="white"
                                            >{{ $t("printing.active") }}</v-chip
                                        >
                                        <v-chip v-else small outlined>{{
                                            $t("printing.inactive")
                                        }}</v-chip>
                                    </template>
                                    <template v-slot:item.action="{ item }">
                                        <v-btn
                                            v-if="isAdmin"
                                            icon
                                            outlined
                                            small
                                            color="primary"
                                            @click="openEditPrinter(item)"
                                        >
                                            <v-icon small>edit</v-icon>
                                        </v-btn>
                                    </template>
                                </v-data-table>
                            </v-container>
                        </v-tab-item>

                        <!-- Print queue tab -->
                        <v-tab-item>
                            <v-container fluid>
                                <v-row class="ma-0 mb-2" align="center">
                                    <v-col cols="6" md="3" class="py-0">
                                        <v-select
                                            v-model="filterState"
                                            :items="stateChoices"
                                            :label="$t('printing.state')"
                                            clearable
                                            dense
                                            outlined
                                            hide-details
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="6" md="3" class="py-0">
                                        <v-select
                                            v-model="filterPrinter"
                                            :items="printerChoices"
                                            :label="$t('printing.printer')"
                                            clearable
                                            dense
                                            outlined
                                            hide-details
                                        ></v-select>
                                    </v-col>
                                    <v-spacer></v-spacer>
                                    <v-btn
                                        icon
                                        small
                                        class="mr-2"
                                        @click="refreshJobs"
                                    >
                                        <v-icon>refresh</v-icon>
                                    </v-btn>
                                </v-row>
                                <v-data-table
                                    :headers="jobHeaders"
                                    :items="
                                        tenantPrintJobs.edges.map(
                                            edge => edge.node
                                        )
                                    "
                                    :options.sync="jobTableOptions"
                                    :server-items-length="
                                        tenantPrintJobs.totalCount
                                    "
                                    :loading="
                                        $apollo.queries.tenantPrintJobs.loading
                                    "
                                    :footer-props="{
                                        'items-per-page-options': [10, 25]
                                    }"
                                    disable-sort
                                    :no-data-text="$t('printing.no_jobs')"
                                >
                                    <template v-slot:item.createdAt="{ item }">
                                        {{ formatDateTime(item.createdAt) }}
                                    </template>
                                    <template v-slot:item.printer="{ item }">
                                        {{ item.printer ? item.printer.name : "" }}
                                    </template>
                                    <template v-slot:item.state="{ item }">
                                        <v-chip
                                            small
                                            :color="stateColor(item.state)"
                                            text-color="white"
                                            >{{
                                                $t(
                                                    "printing.state_" +
                                                        item.state
                                                )
                                            }}</v-chip
                                        >
                                        <div
                                            v-if="item.detail"
                                            class="caption grey--text"
                                        >
                                            {{ item.detail }}
                                        </div>
                                    </template>
                                    <template v-slot:item.action="{ item }">
                                        <v-btn
                                            v-if="
                                                item.state === 'failed' ||
                                                    item.state === 'expired'
                                            "
                                            icon
                                            outlined
                                            small
                                            color="primary"
                                            :title="$t('printing.retry')"
                                            @click="retryJob(item)"
                                        >
                                            <v-icon small>replay</v-icon>
                                        </v-btn>
                                        <v-btn
                                            v-if="item.state === 'queued'"
                                            icon
                                            outlined
                                            small
                                            color="error"
                                            :title="$t('printing.cancel_job')"
                                            @click="cancelJob(item)"
                                        >
                                            <v-icon small>close</v-icon>
                                        </v-btn>
                                    </template>
                                </v-data-table>
                            </v-container>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import moment from "moment";

import DeleteDialog from "@/components/base/DeleteDialog.vue";
import PrintAgentCreatedDialog from "@/components/printing/PrintAgentCreatedDialog.vue";
import {
    CANCEL_PRINT_JOB,
    CREATE_PRINT_AGENT,
    CREATE_PRINTER,
    PRINT_AGENTS_QUERY,
    PRINT_JOB_STATE_COLORS,
    RETRY_PRINT_JOB,
    REVOKE_PRINT_AGENT,
    TENANT_PRINT_JOBS_QUERY,
    TENANT_PRINTERS_QUERY,
    UPDATE_PRINT_AGENT,
    UPDATE_PRINTER
} from "@/graphql/printing.js";

export default {
    name: "printing-page",
    components: {
        DeleteDialog,
        PrintAgentCreatedDialog
    },

    apollo: {
        printAgents: {
            query: PRINT_AGENTS_QUERY,
            fetchPolicy: "cache-and-network",
            update: data => data.printAgents || [],
            pollInterval: 60000,
            skip() {
                return !this.isAdmin;
            }
        },
        tenantPrinters: {
            query: TENANT_PRINTERS_QUERY,
            fetchPolicy: "cache-and-network",
            update: data => data.tenantPrinters,
            pollInterval: 60000
        },
        tenantPrintJobs: {
            query: TENANT_PRINT_JOBS_QUERY,
            fetchPolicy: "cache-and-network",
            update: data => data.tenantPrintJobs,
            pollInterval: 30000,
            // Static initial variables only — every later change goes
            // through refreshJobsPage()/setVariables() so there is a single
            // source of truth for the pagination variables.
            variables: { first: 25 }
        }
    },

    data() {
        return {
            tab: 0,

            printAgents: [],
            tenantPrinters: { edges: [] },
            tenantPrintJobs: { edges: [], totalCount: 0, pageInfo: {} },

            // agents
            createAgentDialog: false,
            createAgentFormValid: false,
            isCreatingAgent: false,
            newAgentName: "",
            agentCreatedDialog: false,
            createdRawKey: "",
            createdAgentId: null,
            renameAgentDialog: false,
            renameAgentName: "",
            renamedAgent: null,
            isSavingAgent: false,
            revokeAgentDialog: false,
            revokedAgent: null,

            // printers
            printerDialog: false,
            printerFormValid: false,
            isSavingPrinter: false,
            editedPrinter: null,

            // queue
            filterState: null,
            filterPrinter: null,
            jobTableOptions: { page: 1, itemsPerPage: 25 },
            jobPage: 1
        };
    },

    computed: {
        me() {
            return this.$store.state.user.me;
        },
        isAdmin() {
            return !!(this.me && this.me.tenantUser && this.me.tenantUser.isAdmin);
        },
        activeAgents() {
            return (this.printAgents || []).filter(agent => !agent.revoked);
        },
        printers() {
            return (this.tenantPrinters.edges || []).map(edge => edge.node);
        },
        agentChoices() {
            return this.activeAgents.map(agent => ({
                text: agent.name,
                value: agent.id
            }));
        },
        printerChoices() {
            return this.printers.map(printer => ({
                text: printer.name,
                value: printer.id
            }));
        },
        stateChoices() {
            return [
                "queued",
                "sent",
                "printed",
                "failed",
                "expired",
                "cancelled"
            ].map(state => ({
                text: this.$t("printing.state_" + state),
                value: state
            }));
        },
        agentHeaders() {
            return [
                { text: this.$t("printing.agent_name"), value: "name" },
                {
                    text: this.$t("printing.status"),
                    value: "isOnline",
                    sortable: false
                },
                {
                    text: this.$t("printing.printers"),
                    value: "printerCount",
                    sortable: false
                },
                { text: this.$t("printing.created"), value: "created" },
                {
                    text: this.$t("printing.actions"),
                    value: "action",
                    sortable: false,
                    align: "center",
                    width: "110px"
                }
            ];
        },
        printerHeaders() {
            const headers = [
                { text: this.$t("printing.printer_name"), value: "name" },
                { text: this.$t("printing.printer_model"), value: "model" },
                { text: this.$t("printing.serial"), value: "serial" },
                {
                    text: this.$t("printing.printer_address"),
                    value: "address",
                    sortable: false
                },
                {
                    text: this.$t("printing.served_by_agent"),
                    value: "servedByAgentName",
                    sortable: false
                },
                { text: "DPI", value: "dpi", sortable: false },
                { text: this.$t("printing.status"), value: "isActive" }
            ];
            if (this.isAdmin) {
                headers.push({
                    text: this.$t("printing.actions"),
                    value: "action",
                    sortable: false,
                    align: "center",
                    width: "70px"
                });
            }
            return headers;
        },
        jobHeaders() {
            return [
                { text: this.$t("printing.time"), value: "createdAt" },
                { text: this.$t("printing.label"), value: "summary" },
                { text: this.$t("printing.printer"), value: "printer" },
                { text: this.$t("printing.requested_by"), value: "createdBy" },
                { text: this.$t("printing.state"), value: "state" },
                {
                    text: this.$t("printing.actions"),
                    value: "action",
                    align: "center",
                    width: "110px"
                }
            ];
        }
    },

    watch: {
        jobTableOptions: {
            handler(newVal, oldVal) {
                // Vuetify keeps the current page when the items-per-page
                // selector changes; restart from page 1 ourselves.
                if (oldVal && newVal.itemsPerPage !== oldVal.itemsPerPage) {
                    this.resetJobsToFirstPage();
                } else {
                    this.refreshJobsPage(newVal);
                }
            },
            deep: true
        },
        filterState() {
            this.resetJobsToFirstPage();
        },
        filterPrinter() {
            this.resetJobsToFirstPage();
        }
    },

    methods: {
        showSnackbar(color, message) {
            this.$store.dispatch("snackbar/showMessage", { color, message });
        },
        showError(error) {
            console.error(error);
            this.showSnackbar(
                "error",
                error.graphQLErrors && error.graphQLErrors.length
                    ? error.graphQLErrors.map(e => e.message).join(", ")
                    : error.message
            );
        },
        formatDate(dateStr) {
            if (!dateStr) return "";
            return moment(dateStr).format("YYYY-MM-DD");
        },
        formatDateTime(dateStr) {
            if (!dateStr) return "";
            return moment(dateStr).format("YYYY-MM-DD HH:mm");
        },
        formatFromNow(dateStr) {
            if (!dateStr) return "";
            return moment(dateStr).fromNow();
        },
        stateColor(state) {
            return PRINT_JOB_STATE_COLORS[state] || "grey";
        },
        refreshAll() {
            if (this.isAdmin) this.$apollo.queries.printAgents.refresh();
            this.$apollo.queries.tenantPrinters.refresh();
        },
        refreshJobs() {
            this.$apollo.queries.tenantPrintJobs.refresh();
        },

        // ---- agents ----
        openCreateAgent() {
            this.newAgentName = "";
            this.createAgentDialog = true;
        },
        createAgentConfirmed() {
            if (!this.newAgentName || !this.newAgentName.trim()) return;
            this.isCreatingAgent = true;
            this.$apollo
                .mutate({
                    mutation: CREATE_PRINT_AGENT,
                    variables: {
                        input: { name: this.newAgentName.trim() }
                    }
                })
                .then(response => {
                    const payload = response.data.createPrintAgent;
                    this.createdRawKey = payload.rawKey;
                    this.createdAgentId = payload.agent.id;
                    this.createAgentDialog = false;
                    this.agentCreatedDialog = true;
                    this.refreshAll();
                })
                .catch(this.showError)
                .finally(() => {
                    this.isCreatingAgent = false;
                });
        },
        openRenameAgent(agent) {
            this.renamedAgent = agent;
            this.renameAgentName = agent.name;
            this.renameAgentDialog = true;
        },
        renameAgentConfirmed() {
            if (!this.renameAgentName || !this.renameAgentName.trim()) return;
            this.isSavingAgent = true;
            this.$apollo
                .mutate({
                    mutation: UPDATE_PRINT_AGENT,
                    variables: {
                        input: {
                            id: this.renamedAgent.id,
                            name: this.renameAgentName.trim()
                        }
                    }
                })
                .then(() => {
                    this.renameAgentDialog = false;
                    this.refreshAll();
                    this.showSnackbar("success", this.$t("printing.saved"));
                })
                .catch(this.showError)
                .finally(() => {
                    this.isSavingAgent = false;
                });
        },
        openRevokeAgent(agent) {
            this.revokedAgent = agent;
            this.revokeAgentDialog = true;
        },
        revokeAgentConfirmed(agent) {
            if (!agent) return;
            this.$apollo
                .mutate({
                    mutation: REVOKE_PRINT_AGENT,
                    variables: { input: { id: agent.id } }
                })
                .then(() => {
                    this.revokeAgentDialog = false;
                    this.refreshAll();
                    this.showSnackbar(
                        "success",
                        this.$t("printing.agent_revoked")
                    );
                })
                .catch(error => {
                    this.showError(error);
                    this.revokeAgentDialog = false;
                });
        },

        // ---- printers ----
        openCreatePrinter() {
            this.editedPrinter = {
                id: null,
                name: "",
                address: "",
                dpi: 203,
                isActive: true,
                isAutoDiscovered: false,
                // exactly one agent -> preselect it (backend enforces too)
                servedByAgentId:
                    this.agentChoices.length === 1
                        ? this.agentChoices[0].value
                        : null
            };
            this.printerDialog = true;
        },
        openEditPrinter(printer) {
            this.editedPrinter = {
                id: printer.id,
                name: printer.name,
                address: printer.address,
                dpi: printer.dpi,
                isActive: printer.isActive,
                isAutoDiscovered: printer.isAutoDiscovered,
                servedByAgentId: null
            };
            this.printerDialog = true;
        },
        savePrinter() {
            const printer = this.editedPrinter;
            if (!printer || !printer.name || !printer.name.trim()) return;
            this.isSavingPrinter = true;

            let mutation, input;
            if (printer.id) {
                mutation = UPDATE_PRINTER;
                input = {
                    id: printer.id,
                    name: printer.name.trim(),
                    dpi: printer.dpi,
                    isActive: printer.isActive
                };
                if (!printer.isAutoDiscovered) {
                    input.address = printer.address.trim();
                    if (printer.servedByAgentId) {
                        input.servedByAgentId = printer.servedByAgentId;
                    }
                }
            } else {
                mutation = CREATE_PRINTER;
                input = {
                    name: printer.name.trim(),
                    address: printer.address.trim(),
                    dpi: printer.dpi,
                    servedByAgentId: printer.servedByAgentId
                };
            }

            this.$apollo
                .mutate({ mutation, variables: { input } })
                .then(() => {
                    this.printerDialog = false;
                    this.$apollo.queries.tenantPrinters.refresh();
                    this.showSnackbar("success", this.$t("printing.saved"));
                })
                .catch(this.showError)
                .finally(() => {
                    this.isSavingPrinter = false;
                });
        },

        // ---- queue ----
        resetJobsToFirstPage() {
            this.jobPage = 1;
            if (this.jobTableOptions.page !== 1) {
                // Replacing the object triggers the options watcher, which
                // then fetches page 1 with the current filters.
                this.jobTableOptions = { ...this.jobTableOptions, page: 1 };
            } else {
                this.refreshJobsPage(this.jobTableOptions);
            }
        },
        refreshJobsPage(options) {
            const { page, itemsPerPage } = options;
            const variables = {
                first: null,
                last: null,
                before: null,
                after: null,
                state: this.filterState || null,
                printer: this.filterPrinter ? [this.filterPrinter] : null
            };

            if (page < this.jobPage) {
                variables.last = itemsPerPage;
                variables.before = this.tenantPrintJobs.pageInfo.startCursor;
            } else if (page > this.jobPage) {
                variables.first = itemsPerPage;
                variables.after = this.tenantPrintJobs.pageInfo.endCursor;
            } else {
                variables.first = itemsPerPage;
            }

            this.$apollo.queries.tenantPrintJobs.setVariables(variables);
            this.$apollo.queries.tenantPrintJobs.refresh();
            this.jobPage = page;
        },
        retryJob(job) {
            this.$apollo
                .mutate({
                    mutation: RETRY_PRINT_JOB,
                    variables: { input: { id: job.id } }
                })
                .then(() => {
                    this.refreshJobs();
                    this.showSnackbar(
                        "success",
                        this.$t("printing.job_requeued")
                    );
                })
                .catch(this.showError);
        },
        cancelJob(job) {
            this.$apollo
                .mutate({
                    mutation: CANCEL_PRINT_JOB,
                    variables: { input: { id: job.id } }
                })
                .then(() => {
                    this.refreshJobs();
                    this.showSnackbar(
                        "success",
                        this.$t("printing.job_cancelled")
                    );
                })
                .catch(this.showError);
        }
    }
};
</script>
